import axios from "axios";

import { getAPIUrl, getBaseHost } from "../utils/url/url";
import { getTokens, setTokens } from "../utils/systemUtils/systemUtils";
import { APIUrls } from "../utils/appServer/appServer";
import { logNetworkEvent } from "./analitics";
import { isProduction } from "../utils/assembly";
import { localStorageSafe, setLocalStorageItem } from "../utils/storage/storage";
import { consoleLogWarning } from "../utils/console";
import { CUSTOM_EVENT_CONTENT_LOGOUT } from "../utils/storage/storage.constants";
import { dispatchCustomEvent } from "../utils/dom";
import { setRequestCancelTokenSource } from "./utils";
import { rewriteUrl } from "../utils/url/urlRewrite";

const MEMORY_CACHE_TIME = 1000 * 60 * 10; // 10 minutes
const STORAGE_CACHE_TIME = 1000 * 60 * 60 * 24 * 7; //cache for one week

const TAG_NETWORK = "NetworkLog_";
export const CACHE_PREFIX = "net_cache.";

export class NetworkError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

function storeCache(name, data) {
  if (window && localStorageSafe) {
    let json = {
      timestamp: Date.now(),
      data,
    };
    setLocalStorageItem(name, JSON.stringify(json));
  }
}

export function fetchToken(refresh) {
  const {
    accessToken,
    refreshToken,
    serverToken,
    expiresTimestamp
  } = getTokens();

  if (!refresh) {
    if (accessToken && Date.now() < expiresTimestamp) {
      return Promise.resolve(accessToken);
    }

    if (!refreshToken && serverToken) {
      return serverToken;
    }
  }

  let tokenLink = getBaseHost() + APIUrls.getToken;
  if (refreshToken) {
    tokenLink += "?token=" + refreshToken;
  }

  const timeStart = Date.now();

  return axios
    .get(tokenLink)
    .then((response) => {
      const requestTime = Date.now() - timeStart;
      if (requestTime > 3000) {
        logNetworkEvent({
          url: "?token=refreshToken",
          method: "get",
          status: "success",
          time: Date.now() - timeStart,
          code: response.status,
        });
        consoleLogWarning("Base API", `Long request ${(requestTime / 1000).toFixed(1)}s`, {
          requestTime,
          tokenLink,
        });
      }
      let {
        access_token,
        refresh_token,
        expires_in
      } = response.data;
      let refToken = refresh_token === undefined ? refreshToken : refresh_token;
      setTokens(access_token, refToken, expires_in);
      return access_token;
    })
    .catch((response) => {
      if (response?.message === "Request failed with status code 400") {
        setTokens();
      }

      logNetworkEvent({
        url: "?token=refreshToken",
        time: Date.now() - timeStart,
        status: "error",
        code: response.response?.status || "unknown",
      });
      return undefined;
    });
}

/**
 * @type {{
 *   [string]: {
 *     timestamp: number,
 *     data: any,
 *   }
 * }}
 */
const MemoryCacheHolder = {};
if (typeof window !== "undefined") {
  window.MemoryCacheHolder = MemoryCacheHolder;
}

/**
 * @typedef {Object} NetworkParams
 * @property {boolean} external
 * @property {!string} url - url, if start without http - adds base API server
 * @property {?string} type=get - get, post, delete, put.
 * @property {?object} data - data for post request
 * @property {?object} headers - additional headers
 * @property {?boolean} skipToken - skip use api token if request for external request
 * @property {?function} parseError - parse error of request
 * @property {?function} parseResponse - parse result of request
 * @property {?boolean} memoryCache - cache get request in memory by url
 * @property {?string} memoryCacheName - cache get request in memory by special name
 * @property {number} memoryCacheTime={@link MEMORY_CACHE_TIME} - set time for cache
 * @property {?string} storageCacheName - Store data in localStorage by name
 * @property {?number} storageCacheTime={@link STORAGE_CACHE_TIME} - set time for cache
 * @property {?string} responseType - e.g. "blob" for zip files
 * @property {?function} onDownloadProgress
 * @property {?Object} cancelTokenKey - key for set request's {axios.CancelToken.source()}
 */

/**
 * base network request for client side
 * @param {NetworkParams} request
 * @param {number} refreshCount
 * @returns
 */
export async function makeRequest(request, refreshCount = 0) {
  if (!request.url) {
    return undefined;
  }

  let headers = request.headers || {};

  if (!request.external) {
    const token = await fetchToken(refreshCount !== 0);

    if (!token) {
      return undefined;
    }

    headers.Authorization = `Bearer ${token}`;
  }

  const requestMethod = request.type || "get";
  let url;
  if (request.url.indexOf("http") === 0) {
    url = request.url;
  } else if (request.url.indexOf("/") === 0) {
    url = getAPIUrl() + request.url;
  } else {
    url = getAPIUrl() + "/" + request.url;
  }
  
  // EGWW-4280
  url = rewriteUrl(url);

  const storageNameWithPrefix = request.storageCacheName
    ? CACHE_PREFIX + request.storageCacheName
    : undefined;
  if (window && localStorageSafe && storageNameWithPrefix) {
    let cache = localStorageSafe.getItem(storageNameWithPrefix);
    if (cache) {
      try {
        const storageTime = request.storageCacheTime || STORAGE_CACHE_TIME;
        let jsonCache = JSON.parse(cache);

        if (jsonCache.data && Date.now() - jsonCache.timestamp < storageTime) {
          return jsonCache.data;
        }
      } catch (e) {
        console.log("Error accessing local storage network cache: ", e);
      }
    }
  }

  let memoryCacheName;
  if (request.memoryCache) {
    if (request.memoryCacheName) {
      memoryCacheName = request.memoryCacheName;
    } else if (requestMethod === "get") {
      memoryCacheName = url;
    }

    const time = request.memoryCacheTime || MEMORY_CACHE_TIME;
    const cachedData = MemoryCacheHolder[memoryCacheName];
    if (cachedData && cachedData.data && Date.now() - cachedData.timestamp < time) {
      return cachedData.data;
    }
  }

  const timeStart = Date.now();

  let cancelTokenSource;
  if (request.cancelTokenKey) {
    cancelTokenSource = axios.CancelToken.source();
    setRequestCancelTokenSource(request.cancelTokenKey, cancelTokenSource);
  }

  const requestBodyFinal = {
    method: requestMethod,
    url,
    headers,
    data: request.data,
    responseType: request.responseType,
    onDownloadProgress: request.onDownloadProgress,
    cancelToken: cancelTokenSource ? cancelTokenSource.token : undefined,
  };

  return axios(requestBodyFinal)
    .then(async (response) => {
      const requestTime = Date.now() - timeStart;

      if (requestTime < 1000000) {
        if (isProduction()) {
          if (requestTime > 3000) {
            logNetworkEvent({
              url,
              method: requestMethod,
              status: "success",
              time: Date.now() - timeStart,
              code: response.status,
              post_data: request.data,
            });
            consoleLogWarning("Base API", `Long request ${(requestTime / 1000).toFixed(1)}s`, {
              requestTime,
              requestBodyFinal
            });
          }
        } else {
          logNetworkEvent({
            url,
            method: requestMethod,
            status: "success",
            time: Date.now() - timeStart,
            code: response.status,
            post_data: request.data,
          });
        }
      }

      let finalResponse = response;

      if (typeof request.parseResponse === "function") {
        finalResponse = await request.parseResponse(response);
      }

      if (storageNameWithPrefix) {
        storeCache(storageNameWithPrefix, finalResponse);
      }

      if (memoryCacheName) {
        MemoryCacheHolder[memoryCacheName] = {
          timestamp: Date.now(),
          data: finalResponse,
        };
      }

      return finalResponse;
    })
    .catch((response) => {
      if (response.__CANCEL__) {
        if (request.onCancel) {
          request.onCancel();
        }
      } else {
        if (response.response) {
          if (response.response.status !== 304) {
            console.log(`${TAG_NETWORK}makeRequest error`, url, response);
          }

          logNetworkEvent({
            url,
            time: Date.now() - timeStart,
            status: "error",
            code: response.response.status,
          });

          if (response.response.status === 401) {
            if (request.external) {
              throw new NetworkError(response.response.status);
            } else {
              const callNumber = refreshCount ? refreshCount + 1 : 1;
              console.log(`%cRefreshing token `, "background: black; color: white;", { callNumber });
              if (callNumber > 5) {
                dispatchCustomEvent(CUSTOM_EVENT_CONTENT_LOGOUT);
                return undefined;
              }
              return makeRequest(request, callNumber);
            }
          }

          if (request.parseError) {
            return request.parseError(response.response);
          }
        }
      }

      return undefined;
    });
}
